import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { getSelectedPeriod } from "../../../services/GroupContext";
import { processDateJS } from "../../../utils/dateProcessor";
import { getPriorPoas } from "../../../utils/PeriodUtils";
import {
  handleAngieDownloadTemplate,
  handleAngieImport,
} from "./angieDataImportUtil";
import {
  handleEbitdaImport,
  handleEbitdaTemplateDownload,
} from "./ebitdaDataImportUtil";
import {
  handleEntityInputImport,
  handleEntityInputTemplateDownload,
} from "./entityInputImportUtil";
import {
  handleGrpCompConfirmationOnWarnings,
  handleGrpCompImport,
  handleGrpCompTemplateDownload,
} from "./GroupCompaniesDataImportUtil";
import { handleGroupDataConfirmation } from "./groupDataImportUtils";
import {
  handleNgieCompImport,
  handleNgieTemplateDownload,
} from "./ngieDataImportUtil";
import {
  handleQngieDownloadTemplate,
  handleQngieImport,
} from "./qngieDataImportUtil";

const enableCellEditing = (worksheet, cellAddress) => {
  const cell = worksheet.getCell(cellAddress);
  cell.protection = { locked: false };
};

const generateRandomPassword = (length = 12) => {
  const charset =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+[]{}|;:,.<>?";
  let password = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  return password;
};

const setValidationsByColumnType = (
  column,
  row,
  cell,
  sheetBuilder = undefined,
) => {
  const currentRowType = sheetBuilder?.[row - 2]?.type;
  const rowValidation = sheetBuilder?.[row - 2]?.validation;
  if (["header", "subheader"].includes(currentRowType) && !rowValidation)
    return;

  if (rowValidation === "fourDecimal")
    setPositiveFourDecimalNumberValidation(cell);
  else if (column.type === "text") setTextValidation(cell);
  else if (column.type === "number") {
    if (rowValidation === "positive") setPositiveNumberValidation(cell);
    else if (rowValidation === "negative") setNegativeNumberValidation(cell);
    else if (column.subtype === "tax_office")
      setLeadingZerosNumberValidation(cell, column);
    else if (column.subtype === "utr") setDashSeparatedNumberValidation(cell);
    else setNumberValidation(cell);
  } else if (column.type === "date") setDateValidation(cell);
};

const setTextValidation = (cell) => {
  cell.style = {
    ...cell.style,
    numFmt: "General",
  };
  cell.dataValidation = {
    type: "custom",
    formulae: [`OR(ISBLANK(${cell.address}), ISTEXT(${cell.address}))`],
    showErrorMessage: true,
    errorTitle: "Invalid Input",
    error: "Please enter text only.",
  };
};

const setLeadingZerosNumberValidation = (cell, col) => {
  cell.numFmt = "@";
  const cellAddress = cell.address;

  cell.dataValidation = {
    type: "custom",
    allowBlank: true,
    formulae: [
      `OR(
                ISBLANK(${cellAddress}),
                AND(
                    ISNUMBER(VALUE(${cellAddress})*1),
                    LEN(SUBSTITUTE(${cellAddress}, " ", ""))=${col.length}
                )
            )`,
    ],
    showErrorMessage: true,
    errorTitle: "Invalid Input",
    error: `Please enter a ${col.length}-digit number.`,
  };
};

const setDashSeparatedNumberValidation = (cell) => {
  cell.numFmt = "@";
  cell.dataValidation = {
    type: "custom",
    formulae: [
      `=OR(
            ISBLANK(${cell.address}),
            AND(
                ISNUMBER(VALUE(SUBSTITUTE(${cell.address}, "-", ""))),
                LEN(SUBSTITUTE(${cell.address}, "-", ""))=10,
                (LEN(${cell.address}) - LEN(SUBSTITUTE(${cell.address}, "-", "")))<=1
            )
        )`,
    ],
    showErrorMessage: true,
    errorTitle: "Invalid Input",
    error: "Please enter a number in the format XXXXX-XXXXX or XXXXXXXXXX.",
  };
};

const setPositiveNumberValidation = (cell) => {
  cell.numFmt = "#,##0.00";
  cell.dataValidation = {
    type: "decimal",
    operator: "greaterThanOrEqual",
    formulae: [0],
    allowBlank: true,
    showErrorMessage: true,
    errorTitle: "Invalid Input",
    error: "Please enter a positive whole number or leave the cell blank.",
  };
};

const setNegativeNumberValidation = (cell) => {
  cell.numFmt = "#,##0.00";
  cell.dataValidation = {
    type: "decimal",
    operator: "lessThanOrEqual",
    formulae: [0],
    allowBlank: true,
    showErrorMessage: true,
    errorTitle: "Invalid Input",
    error: "Please enter a negative whole number or leave the cell blank.",
  };
};

const setDateValidation = (cell) => {
  cell.numFmt = "dd/mm/yyyy";
  cell.dataValidation = {
    type: "date",
    allowBlank: true,
    operator: "between",
    formulae: [new Date(1900, 0, 1), new Date(9999, 11, 31)],
    showErrorMessage: true,
    errorTitle: "Invalid Date",
    error: "Please enter a valid date.",
  };
};

const headerFill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFBFBFBF" }, // Medium grey
  // fgColor: { argb: 'FFF05A28' } // Medium orange
  // fgColor: { argb: 'FFFB7C4D' } // PwC Medium orange
};

const subHeaderFill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" }, // Light grey
  // fgColor: { argb: 'FFF78F6C' } // Light orange
  // fgColor: { argb: 'FFFEDACC' } // PwC Light orange
};

const orangeFill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFEDACC" }, // PwC Light orange
  // fgColor: { argb: 'FFF78F6C' } // Light orange
  // fgColor: { argb: 'FFD9D9D9' } // Light grey
};

const whiteFill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFFFFFF" },
};

const headerBorder = {
  top: { style: "medium" },
  bottom: { style: "medium" },
  left: { style: "thin" },
  right: { style: "thin" },
};

const regularBorder = {
  // top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
  // left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
  // bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
  // right: { style: 'thin', color: { argb: 'FFD9D9D9' } }
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

const regularBlackBorder = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

const getCellValue = (cell) => {
  // get actual value even if cell contains a formula (then, extract result)
  return cell?.result ?? cell?.value?.result ?? cell?.value ?? "";
};

export const setPositiveFourDecimalNumberValidation = (cell) => {
  cell.numFmt = "#,##0.0000";
  cell.dataValidation = {
    type: "decimal",
    operator: "greaterThanOrEqual",
    formulae: [0],
    allowBlank: true,
    showErrorMessage: true,
    errorTitle: "Invalid Input",
    error: "Please enter a positive whole number or leave the cell blank.",
  };
};

export const setNumberValidation = (cell) => {
  cell.numFmt = "#,##0.00";
  cell.dataValidation = {
    type: "decimal",
    allowBlank: true,
    showErrorMessage: true,
    errorTitle: "Invalid Input",
    error: "Please enter a number or leave the cell blank.",
  };
};

export const setWholeNumberValidation = (cell) => {
  cell.numFmt = "#,##0";
  cell.dataValidation = {
    type: "whole",
    allowBlank: true,
    showErrorMessage: true,
    errorTitle: "Invalid Input",
    error: "Please enter a number or leave the cell blank.",
  };
};

export const setNegativeWholeNumberValidation = (cell) => {
  cell.numFmt = "#,##0";
  cell.dataValidation = {
    type: "whole",
    operator: "lessThanOrEqual",
    formulae: [0],
    allowBlank: true,
    showErrorMessage: true,
    errorTitle: "Invalid Input",
    error: "Please enter a negative number or leave the cell blank.",
  };
};

export const setPercentageValidation = (cell) => {
  cell.numFmt = "0.0%";
  cell.dataValidation = {
    type: "decimal",
    operator: "between",
    formula1: 0, // 0% minimum
    formula2: 1, // 100% maximum
    allowBlank: true,
    showErrorMessage: true,
    errorTitle: "Invalid Input",
    error:
      "Please enter a percentage between 0% and 100% or leave the cell blank.",
  };
};

export const enableCellEditingByIndex = (worksheet, rowIndex, colIndex) => {
  const cell = worksheet.getRow(rowIndex).getCell(colIndex);
  cell.protection = { locked: false };
  cell.fill = whiteFill;
  cell.border = regularBorder;
};

export const getDataImportHandlers = (pageName) => {
  const pagesHandlers = {
    groupCompanies: {
      pageName: "Group Companies",
      templateDownloadHandler: handleGrpCompTemplateDownload,
      importHandler: handleGrpCompImport,
      confirmationHandler: handleGrpCompConfirmationOnWarnings,
    },
    ngie: {
      pageName: "NGIE",
      templateDownloadHandler: handleNgieTemplateDownload,
      importHandler: handleNgieCompImport,
      confirmationHandler: handleGroupDataConfirmation,
    },
    entityInput: {
      pageName: "Entity Input",
      templateDownloadHandler: handleEntityInputTemplateDownload,
      importHandler: handleEntityInputImport,
    },
    angie: {
      pageName: "ANGIE",
      templateDownloadHandler: handleAngieDownloadTemplate,
      importHandler: handleAngieImport,
      confirmationHandler: handleGroupDataConfirmation,
    },
    qngie: {
      pageName: "QNGIE",
      templateDownloadHandler: handleQngieDownloadTemplate,
      importHandler: handleQngieImport,
      confirmationHandler: handleGroupDataConfirmation,
    },
    ebitda: {
      pageName: "Group-EBITDA",
      templateDownloadHandler: handleEbitdaTemplateDownload,
      importHandler: handleEbitdaImport,
      confirmationHandler: handleGroupDataConfirmation,
    },
  };

  const handlers = pagesHandlers[pageName];
  return handlers;
};

export const createExcelDocument = (worksheetName, headers) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook?.addWorksheet(worksheetName);
  if (headers?.length) {
    worksheet?.addRows([headers]);
  }
  return { workbook, worksheet };
};

export const saveExcelFile = async (workbook, fileName) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const blob = new Blob([buffer], { type: fileType });
  saveAs(blob, `${fileName}.xlsx`);
};

export const addValidations = ({
  worksheet,
  columnsConfig,
  sheetBuilder = undefined,
  existingDataLength = 0,
}) => {
  const start = existingDataLength + 2;
  for (let row = start; row < start + 300; row++) {
    for (let column of columnsConfig) {
      const cell = worksheet.getCell(`${column.name}${row}`);
      setValidationsByColumnType(column, row, cell, sheetBuilder);
    }
  }
};

export const addGroupCompaniesTableStyles = (
  worksheet,
  numOfColumns,
  existingDataLength,
) => {
  const headersRow = worksheet.getRow(1);
  headersRow.font = { bold: true };
  headersRow.eachCell((cell) => {
    cell.fill = headerFill;
    cell.border = regularBorder;
  });

  for (let row = 2; row <= existingDataLength + 1; row++) {
    for (let col = 1; col <= numOfColumns; col++) {
      const cell = worksheet.getRow(row).getCell(col);
      cell.fill = subHeaderFill;
      cell.border = regularBorder;
    }
  }

  for (let col = 1; col <= numOfColumns; col++) {
    const column = worksheet.getColumn(col);
    column.width = 20;
    column.alignment = { vertical: "top", horizontal: "left", wrapText: true };
  }
};

export const getWorksheetFromFile = async (file, worksheetName = null) => {
  try {
    const buffer = await file.originFile.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);

    const worksheet = worksheetName
      ? workbook.getWorksheet(worksheetName)
      : workbook.worksheets[0];

    if (!worksheet) throw new Error();

    return worksheet;
  } catch (e) {
    throw new Error(
      JSON.stringify({
        missingWorkSheetErrorMessage: `Cannot find Work Sheet with the name: ${worksheetName}`,
      }),
    );
  }
};

export const getWorksheetHeaders = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  const headers = headerRow.values.slice(1);
  return headers;
};

export const parseExcelFileToJson = (worksheet) => {
  const json = [];

  const headers = [];
  worksheet.getRow(1).eachCell((cell, colNumber) => {
    headers.push(cell.value);
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // skip header row
    const rowData = {};
    row.eachCell((cell, colNumber) => {
      const header = headers[colNumber - 1];
      rowData[header] = cell.value;
    });
    json.push(rowData);
  });

  return json;
};

export const filterNewRows = (rows) => {
  return rows
    .filter((row) => !row["Disabled"])
    .map((row) => {
      const { Disabled, ...rowWithoutDisabledColumn } = row;
      return rowWithoutDisabledColumn;
    });
};

export const addColumnValues = async ({
  worksheet,
  worksheetName,
  column,
  values,
  noHeaders = false,
  columnIndex = null,
}) => {
  const increment = noHeaders ? 1 : 2;
  const effectiveWorksheetName = worksheetName || worksheet?.name;

  if (
    ["NGIE", "ANGIE", "QNGIE", "Group-EBITDA"].includes(
      effectiveWorksheetName,
    )
  ) {
    worksheet.getCell(`A1`).value = effectiveWorksheetName;
  }

  values.forEach((val, rowIndex) => {
    if (columnIndex) {
      worksheet.getRow(rowIndex + increment).getCell(columnIndex).value = val;
    } else {
      worksheet.getCell(`${column}${rowIndex + increment}`).value = val;
    }
  });
};

const groupDataColumns = [1, 2, 4, 5, 6, 7];

export const addGroupDataTableStyles = (
  worksheet,
  sheetBuilder,
  isNgie = false,
  isImportTemplate,
  includeReviewColumns = true,
) => {
  worksheet.getRow(1).font = { bold: true };
  worksheet.getColumn(1).width = isNgie ? 90 : 60;
  worksheet.getColumn(2).width = 20;

  if (isImportTemplate && includeReviewColumns) {
    worksheet.getColumn(4).width = 40;
    worksheet.getColumn(5).width = 40;
    worksheet.getColumn(6).width = 40;
    worksheet.getColumn(7).width = 40;
  }

  if (isImportTemplate) {
    // freeze cols A & B and rows up to 8
    worksheet.views = [
      {
        state: "frozen",
        xSplit: 2,
        ySplit: 9,
      },
    ];
  }

  const columns =
    isImportTemplate && includeReviewColumns ? groupDataColumns : [1, 2];
  columns.forEach((col) => {
    worksheet.getCell(1, col).fill = headerFill;
    const column = worksheet.getColumn(col);
    column.alignment = {
      vertical: "center",
      horizontal: "left",
      wrapText: true,
    };

    // set Col D, E, F, G styles
    const headerRows = [2, 4, 6, 9];
    if (isImportTemplate && col > 2) {
      headerRows.forEach(
        (row) => (worksheet.getCell(row, col).font = { bold: true }),
      );
    }

    const explicitlyOrangeRows = [3, 5, 7];

    sheetBuilder.forEach((row, index) => {
      const rowIndex = index + 2;
      const cell = worksheet.getCell(rowIndex, col);
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
      if (row?.type) {
        if (col === 1) cell.font = { bold: true };
        if (row?.type === "header") {
          cell.fill = headerFill;

          cell.border = headerBorder;
        } else if (row?.type === "subheader") {
          cell.fill = subHeaderFill;
        }

        if (
          isImportTemplate &&
          col > 2 &&
          explicitlyOrangeRows.includes(rowIndex)
        ) {
          cell.fill = orangeFill;
        }
      } else if (
        isImportTemplate &&
        col > 2 &&
        !headerRows.includes(rowIndex)
      ) {
        cell.fill = orangeFill;
      }
    });
  });
};

export const addEntityInputTableStyles = (worksheet, sheetBuilder, columns) => {
  worksheet.getColumn(1).width = 100;
  for (let col = 2; col <= columns; col++) {
    worksheet.getColumn(col).width = 30;
  }

  const baseAlignment = {
    wrapText: true,
    vertical: "top",
    horizontal: "left",
  };

  sheetBuilder.forEach((sheetRow, index) => {
    const row = worksheet.getRow(index + 1);

    const hasMetricOrCalc =
      sheetRow.capMetricId || sheetRow.compMetricId || sheetRow.calculation;
    const isHeader = sheetRow.type === "header";
    const isItalic = sheetRow.type === "italic";

    for (let col = 1; col <= columns; col++) {
      const cell = row.getCell(col);

      cell.alignment = baseAlignment;

      if (hasMetricOrCalc) {
        cell.fill = subHeaderFill;
        cell.border = regularBlackBorder;
      }

      if (isHeader) {
        cell.font = { bold: true };
        cell.border = headerBorder;
        cell.fill = headerFill;
      } else if (isItalic) {
        cell.font = { italic: true };
      }
    }
  });
};

export const addGroupDataFileProtection = async (
  worksheet,
  sheetBuilder,
  includeReviewColumns = true,
) => {
  const columnsToProtect = includeReviewColumns ? groupDataColumns : [1, 2];

  [...columnsToProtect, 8, 9, 10, 11, 12, 13].forEach((col) => {
    const colChar = String.fromCharCode(col + 64);
    disableColumnEditing(worksheet, colChar);

    sheetBuilder.forEach((row, index) => {
      if (col > 2 || row.editable) {
        enableCellEditing(worksheet, `${colChar}${index + 2}`);
      }
    });
  });

  await protectWorksheet({ worksheet, insertRows: true });
};

export const addEntityInputSheetProtection = async (
  worksheet,
  sheetBuilder,
  caps,
) => {
  for (let col = 1; col <= caps?.length + 1; col++) {
    disableColumnEditing(worksheet, col);
  }

  const selectedPeriod = getSelectedPeriod();

  const capEditabilityMap = new Map();
  caps.forEach((cap, colIndex) => {
    const capNonEditable = Boolean(
      getPriorPoas(cap, selectedPeriod)?.length !== 0,
    );
    capEditabilityMap.set(colIndex, {
      isNonEditable: capNonEditable,
      onesourceSync: cap.onesource_sync,
    });
  });

  sheetBuilder.forEach((row, rowIndex) => {
    if (row.editable) {
      caps.forEach((cap, colIndex) => {
        const capInfo = capEditabilityMap.get(colIndex);
        if (
          !(
            capInfo.isNonEditable ||
            (row.onesourceNonEditable && capInfo.onesourceSync)
          )
        ) {
          enableCellEditingByIndex(worksheet, rowIndex + 1, colIndex + 2);
        }
      });
    }
  });
  await protectWorksheet({ worksheet, insertRows: false });
};

export const hideColumn = (worksheet, columnIndexOrLetter) => {
  const column = worksheet.getColumn(columnIndexOrLetter);
  column.hidden = true;
};

export async function extractKeyValuePairsFromExcel(worksheet, sheetBuilder) {
  const dto = {};
  const descriptionWithoutValueWarnings = [];
  const valueWithoutDescriptionWarnings = [];

  let key = null;
  worksheet.eachRow((row) => {
    const cellC = row.getCell(3).value;
    if (!["rowType", "header"].includes(cellC)) {
      key = cellC ? cellC.toString().trim() : key;

      if (!cellC) {
        if (!dto?.hasOwnProperty(key)) {
          dto[key] = [];
        }
        const description = getCellValue(row.getCell(1));
        const value = getCellValue(row.getCell(2));
        dto[key].push({
          description,
          value,
        });
      }
    }
  });

  Object.entries(dto).forEach(([key, entries]) => {
    if (key && key !== "null") {
      // Filter out entries without Values
      const filtered = entries.filter((entry) => entry.value);

      dto[key] =
        filtered.length > 0 ? filtered : [{ description: "", value: "" }];

      // Check for description populated but value empty
      const hasDescriptionWithoutValue = entries.some(
        (entry) =>
          entry.description?.toString().trim() &&
          !Number(entry.value?.toString().trim()),
      );

      // Check for value populated but description empty (given more than one row)
      const hasValueWithoutDescription =
        filtered.length > 1 &&
        filtered?.slice(1)?.some((entry) => !entry.description?.trim());

      const label = sheetBuilder?.find((row) => row.key === key)?.label;

      if (hasDescriptionWithoutValue) {
        descriptionWithoutValueWarnings.push(label ? `- ${label}` : "");
      }

      if (hasValueWithoutDescription) {
        valueWithoutDescriptionWarnings.push(label ? `- ${label}` : "");
      }
    } else {
      delete dto[key];
    }
  });

  const warnings = [
    ...(descriptionWithoutValueWarnings?.length > 0
      ? [
          {
            header:
              "The following sections have missing values - rows with missing values will not be imported.",
            messages: descriptionWithoutValueWarnings,
          },
        ]
      : []),
    ...(valueWithoutDescriptionWarnings?.length > 0
      ? [
          {
            header:
              "The following sections have missing descriptions - a description must be provided for every value.",
            messages: valueWithoutDescriptionWarnings,
          },
        ]
      : []),
  ];

  const keys = sheetBuilder.flatMap((row) => (row.key ? [row.key] : []));
  const results = Object.fromEntries(
    Object.entries(dto).filter(([key]) => keys.includes(key)),
  );

  return { results, warnings };
}

export function freezeTopRowAndColumnA({
  worksheet,
  rowCount = 1,
  colCount = 1,
}) {
  worksheet.views = [
    {
      state: "frozen",
      xSplit: colCount, // Freeze Column A
      ySplit: rowCount, // Freeze Row 1
    },
  ];
}

export function extractEntityInputCapsFromExcel(worksheet, sheetBuilder) {
  const caps = [];
  worksheet?.columns?.forEach((col, colIndex) => {
    if (colIndex > 0) {
      const cap = {};
      col.eachCell((cell, rowIndex) => {
        // rowIndex starts at 1 in excelJS
        const { capMetricId, type } = sheetBuilder[rowIndex - 1];
        const val = getCellValue(cell);
        if (capMetricId) {
          if (type === "date" && cell.value) {
            cap[capMetricId] = processDateJS(val);
          } else if (type === "boolean") {
            cap[capMetricId] = typeof val === "boolean" ? val : val === "TRUE";
          } else {
            // convert ct_rate_override from percentage (on Template) to on app state (multiplied by 100)
            cap[capMetricId] =
              capMetricId === "ct_rate_override" ? val * 100 : val;
          }
        }
      });
      caps.push(cap);
    }
  });
  return caps;
}

export function getRangeSumFormula({
  columnLetter,
  rangeStart,
  rangeEnd,
  referenceCells,
}) {
  const baseRange = `${columnLetter}${rangeStart}:${columnLetter}${rangeEnd}`;
  const referenceCellsFormat = referenceCells
    ?.map((cell) => `VALUE(${columnLetter}${cell})`)
    .join(", ");

  return {
    formula: referenceCells
      ? `SUM(${baseRange},${referenceCellsFormat})`
      : `SUM(${baseRange})`,
  };
}

export function getListSumFormula({ columnLetter, rowList }) {
  const cellRefs = rowList.map((row) => `${columnLetter}${row}`);
  return { formula: `SUM(${cellRefs.join(",")})` };
}

export function addGroupDataSubheaderCells({
  list,
  key,
  config,
  data,
  isImportTemplate = true,
}) {
  // given the following sructure: data[key] = [{description: '', value: ''}]
  const metricList = data?.[key]?.filter((metric) => metric.value);
  const newRows = metricList?.map((metric) => ({
    label: metric.description ?? "",
    validation: config.type ?? "",
    value: metric.value,
    editable: true,
  }));

  const validationLabel = isImportTemplate
    ? config.type === "positive"
      ? "Positive values"
      : config.type === "negative"
        ? "(Negative values)"
        : ""
    : "";
  list.push({
    label: config.label,
    type: "subheader",
    key,
    value: validationLabel,
    notes: config.notes ?? [],
    isTotal: { cellCount: (newRows?.length ?? 0) + 3 },
  });
  list.push(
    ...(newRows ?? []),
    ...(isImportTemplate
      ? [
          { label: "", editable: true, validation: config.type ?? "" },
          { label: "", editable: true, validation: config.type ?? "" },
          { label: "", editable: true, validation: config.type ?? "" },
        ]
      : []),
  );
}

export function addNoteToCell({ worksheet, notesList, cellAddress }) {
  if (notesList?.length) {
    const cell = worksheet.getCell(cellAddress);
    cell.note = {
      texts: [
        {
          font: { size: 9, color: { argb: "FF000000" }, name: "Calibri" },
          text: notesList.join("\n"),
        },
      ],
      margins: {
        insetmode: "auto",
        inset: [0.13, 0.13, 0.25, 0.25],
      },
      protection: {
        locked: true,
        lockText: true,
      },
    };
  }
}

export const disableColumnEditing = (worksheet, columnLetter) => {
  worksheet.getColumn(columnLetter).eachCell({ includeEmpty: true }, (cell) => {
    cell.protection = { locked: true };
  });
};

export const protectWorksheet = async ({ worksheet, insertRows = false }) => {
  const password = generateRandomPassword();
  await worksheet.protect(password, {
    selectLockedCells: true,
    selectUnlockedCells: true,
    insertRows,
  });
};

export const addInstructionsWorksheet = ({ workbook, sheetBuilder }) => {
  const sheetName = "Instructions";
  if (!workbook.getWorksheet(sheetName)) {
    const worksheet = workbook?.addWorksheet(sheetName);

    const values = sheetBuilder.map((row) => row.value);
    addColumnValues({
      worksheet,
      column: "A",
      values,
      noHeaders: true,
    });

    // add Styles
    sheetBuilder.forEach((row, index) => {
      const cell = worksheet.getCell(`A${index + 1}`);
      if (row.type === "header") {
        cell.font = { ...cell.font, bold: true };
      }
      if (row.styles?.includes("underline"))
        cell.font = { ...cell.font, underline: "single" };
      if (row.styles?.includes("italic"))
        cell.font = { ...cell.font, italic: true };

      cell.alignment = { wrapText: true };
    });

    const columnA = worksheet.getColumn(1);
    columnA.width = 90;
  }
};
